import {
  Alert,
  Button,
  PageHeader,
  Pagination,
  SegmentedControl,
  Skeleton,
} from '@ntconsult/aluma-react';
import { SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportPercentageCheck } from '../hooks/useExportPercentageCheck';
import { usePaginationLabels } from '../hooks/usePaginationLabels';
import { usePercentageCheck } from '../hooks/usePercentageCheck';
import {
  PERCENTAGE_CHECK_COLUMN_LABEL_KEYS,
  usePercentageCheckColumns,
} from '../hooks/usePercentageCheckColumns';
import {
  PERCENTAGE_CHECK_MAX_PERIOD_DAYS,
  usePercentageCheckFilters,
} from '../hooks/usePercentageCheckFilters';
import type { PercentageCheckBucket, PercentageCheckRow } from '../types/PercentageCheck';
import { formatDisplayDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { ColumnsPicker } from './ColumnsPicker';
import { DateRangeField } from './DateRangeField';
import { ExportButton } from './ExportButton';
import { NoIdentityState } from './NoIdentityState';
import { PercentageCheckFilterChips } from './PercentageCheckFilterChips';
import { PercentageCheckFiltersDrawer } from './PercentageCheckFiltersDrawer';
import { PercentageCheckSummaryCards } from './PercentageCheckSummaryCards';
import { PercentageCheckTable } from './PercentageCheckTable';
import { PerPageSelect } from './PerPageSelect';
import { PINNED_BLOCK, WIDE_PAGE } from './wideTable';

export function PercentageCheckPage() {
  const { t, i18n } = useTranslation('alocacao');
  const paginationLabels = usePaginationLabels();
  const filterState = usePercentageCheckFilters();
  const columnsState = usePercentageCheckColumns();
  const query = usePercentageCheck(filterState.filters);
  const exportMutation = useExportPercentageCheck(filterState.filters, columnsState.visibleColumns);

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  const items = useMemo<PercentageCheckRow[]>(() => {
    if (!data) return [];
    if (filterState.state.includeZero) return data.items;
    return data.items.filter((row) => row.bucket !== 'zero');
  }, [data, filterState.state.includeZero]);

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const totalPages = data
    ? Math.max(1, Math.ceil(data.pagination.total / data.pagination.perPage))
    : 1;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_PAGE}`}>
      <div className={PINNED_BLOCK}>
        <PageHeader
          title={t('consultas.conferenciaPercentual.titulo')}
          description={t('consultas.conferenciaPercentual.descricao')}
        />
      </div>

      <section
        className="flex min-w-0 flex-col gap-4"
        aria-label={t('consultas.conferenciaPercentual.titulo')}
      >
        <div className={`flex flex-wrap items-start gap-4 ${PINNED_BLOCK}`}>
          <DateRangeField
            startDate={filterState.state.startDate}
            endDate={filterState.state.endDate}
            onChange={filterState.setPeriod}
            startLabel={t('consultas.conferenciaPercentual.inicio')}
            endLabel={t('consultas.conferenciaPercentual.fim')}
            hint={t('consultas.conferenciaPercentual.dicaPeriodo')}
            maxDays={PERCENTAGE_CHECK_MAX_PERIOD_DAYS}
          />
          <div className="mt-[26px] flex flex-wrap items-center gap-3">
            <SegmentedControl
              aria-label={t('consultas.conferenciaPercentual.colunas.classificacao')}
              value={filterState.filters.bucket ?? 'all'}
              onValueChange={(value) => filterState.setBucket(value as PercentageCheckBucket)}
            >
              <SegmentedControl.Item value="all">
                {t('consultas.conferenciaPercentual.bucket.todos')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="below">
                {t('consultas.conferenciaPercentual.bucket.abaixo')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="at100">
                {t('consultas.conferenciaPercentual.bucket.cemPorcento')}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="above">
                {t('consultas.conferenciaPercentual.bucket.acima')}
              </SegmentedControl.Item>
            </SegmentedControl>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<SlidersHorizontal size={14} />}
              onClick={() => setAdvancedOpen(true)}
            >
              {t('filtros.avancados')}
            </Button>
            <ColumnsPicker
              columnsState={columnsState}
              labelKeys={PERCENTAGE_CHECK_COLUMN_LABEL_KEYS}
            />
            <ExportButton
              exportMutation={exportMutation}
              disabled={!data || data.pagination.total === 0}
              labels={{
                title: t('consultas.conferenciaPercentual.exportarTitulo'),
                allPages: t('exportacao.todasPaginasRegistros', {
                  count: data?.pagination.total ?? 0,
                }),
                currentPage: t('exportacao.paginaAtualRegistros', {
                  count: data?.items.length ?? 0,
                }),
              }}
            />
          </div>
        </div>

        {data && (
          <p className="text-xs text-foreground-muted">
            {t('consultas.conferenciaPercentual.periodoAplicado', {
              periodo: `${formatDisplayDate(data.startDate, i18n.language)} – ${formatDisplayDate(
                data.endDate,
                i18n.language,
              )}`,
            })}
          </p>
        )}

        <div className={PINNED_BLOCK}>
          <PercentageCheckFilterChips
            filterState={filterState}
            filterOptions={data?.filterOptions}
          />
        </div>

        <PercentageCheckSummaryCards summary={data?.summary} isLoading={isListLoading} />

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && data && (
          <div className="flex flex-col gap-4">
            <p className={`text-sm text-foreground-muted ${PINNED_BLOCK}`} aria-live="polite">
              {t('filtros.totalProfissionais', {
                count: data.pagination.total,
                showing: items.length,
              })}
            </p>
            <PercentageCheckTable
              items={items}
              filterOptions={data.filterOptions}
              columnsState={columnsState}
              filterState={filterState}
            />
            <div className={`flex flex-wrap items-center gap-4 ${PINNED_BLOCK}`}>
              <PerPageSelect
                value={filterState.filters.perPage ?? 20}
                onChange={filterState.setPerPage}
              />
              {totalPages > 1 && (
                <Pagination
                  {...paginationLabels}
                  page={filterState.filters.page ?? 1}
                  totalPages={totalPages}
                  onPageChange={filterState.goToPage}
                />
              )}
            </div>
          </div>
        )}
      </section>

      <PercentageCheckFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
        filterOptions={data?.filterOptions}
      />
    </div>
  );
}
